/**
 * Layer merge-down (_docs/tilemap/07 TM5 polish backlog). Composites an
 * "upper" layer's painted tiles onto a "lower" layer in place — a painted
 * upper cell overwrites the lower cell beneath it; an empty upper cell
 * leaves the lower cell untouched (the same visual result as the two layers
 * stacked and viewed together, engine-Z order). Only meaningful between two
 * layers that share a tileset (mixing atlas indices from different tilesets
 * into one layer would render as the wrong tiles — the frozen
 * one-tileset-per-layer contract, _docs/tilemap/01 §3).
 *
 * Pure over the LayerTiles model — no store, no network — so the merge
 * itself is trivially unit-testable; the store action wraps this with the
 * fetch/flush/delete-layer side effects.
 */

import { CHUNK_SIZE } from './constants';
import type { LayerTiles } from './chunk';

/**
 * Merge `upper` down onto `lower`, mutating `lower` in place. Returns the
 * number of cells actually changed (0 means the merge had no visible effect,
 * e.g. `upper` was empty).
 */
export function mergeLayersDown(upper: LayerTiles, lower: LayerTiles): number {
    let changed = 0;
    for (const chunk of upper.allChunks()) {
        const baseX = chunk.cx * CHUNK_SIZE;
        const baseY = chunk.cy * CHUNK_SIZE;
        for (let ly = 0; ly < CHUNK_SIZE; ly++) {
            for (let lx = 0; lx < CHUNK_SIZE; lx++) {
                const id = chunk.tiles[ly * CHUNK_SIZE + lx];
                if (id === 0) continue; // empty upper cell — lower shows through, nothing to do
                if (lower.setTile(baseX + lx, baseY + ly, id)) changed++;
            }
        }
    }
    return changed;
}
